import { Link } from "react-router-dom";

/**
 * Renders an inline paragraph with markdown-style formatting & links parsed
 * to HTML. Order of processing matters — inline emphasis is wrapped FIRST,
 * then links are extracted so link labels can themselves contain emphasis.
 *
 * Supported INLINE syntax:
 *   **text**                                    → <strong>
 *   *text*                                      → <em>
 *   __text__                                    → <u>
 *   [text](https://example.com)                  → external link
 *   [text](https://example.com){nofollow}        → rel="nofollow"
 *   [text](https://example.com){sponsored}       → rel="sponsored"
 *   [text](https://example.com){ugc}             → rel="ugc"
 *   [text](https://example.com){dofollow}        → rel=""  (explicit)
 *   [text](/internal/path)                       → internal <Link>
 *
 * Image syntax ![alt](url) is NOT matched here — handled at paragraph level.
 */

// Walk a string and yield React nodes for **bold**, *italic*, __underline__
// emphasis. Strings between matches stay as plain strings.
function applyInlineEmphasis(text, keyPrefix = "e") {
  if (!text) return [];
  const out = [];
  let i = 0;
  let k = 0;
  // Greedy-but-non-spanning: process LEFT-most match each iteration.
  while (i < text.length) {
    const remaining = text.slice(i);
    // Find the earliest of **, __, * markers
    const candidates = [
      { re: /\*\*([^*\n]+?)\*\*/, tag: "strong" },
      { re: /__([^_\n]+?)__/, tag: "u" },
      { re: /(?<!\*)\*([^*\n]+?)\*(?!\*)/, tag: "em" },
    ];
    let best = null;
    for (const c of candidates) {
      const m = remaining.match(c.re);
      if (m && (best === null || m.index < best.index)) {
        best = { ...c, match: m, index: m.index };
      }
    }
    if (!best) {
      out.push(text.slice(i));
      break;
    }
    if (best.index > 0) out.push(text.slice(i, i + best.index));
    const Tag = best.tag;
    out.push(<Tag key={`${keyPrefix}-${k++}`}>{best.match[1]}</Tag>);
    i += best.index + best.match[0].length;
  }
  return out;
}

export function InlineMarkdown({ text }) {
  if (!text) return null;
  // Step 1: split by links so we can recurse into the surrounding text for
  // emphasis without re-wrapping link labels.
  const parts = [];
  const re = /(?<!!)\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)(?:\{(dofollow|nofollow|sponsored|ugc)\})?/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(...applyInlineEmphasis(text.slice(lastIndex, m.index), `pre-${key}`));
    }
    const [, label, href, relType] = m;
    const isInternal = href.startsWith("/");
    const labelNodes = applyInlineEmphasis(label, `lbl-${key}`);

    if (isInternal) {
      parts.push(
        <Link
          key={`l-${key++}`}
          to={href}
          className="underline underline-offset-4 decoration-[var(--hinton-red)]/40 hover:decoration-[var(--hinton-red)] transition-colors"
        >
          {labelNodes}
        </Link>
      );
    } else {
      const relParts = ["noopener", "noreferrer"];
      if (relType && relType !== "dofollow") {
        relParts.push(relType);
      }
      parts.push(
        <a
          key={`l-${key++}`}
          href={href}
          target="_blank"
          rel={relParts.join(" ")}
          className="underline underline-offset-4 decoration-[var(--hinton-red)]/40 hover:decoration-[var(--hinton-red)] transition-colors"
        >
          {labelNodes}
        </a>
      );
    }
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(...applyInlineEmphasis(text.slice(lastIndex), `tail-${key}`));
  }
  return <>{parts}</>;
}
